import { useRef, useState } from "react";
import { Upload, Link as LinkIcon, X } from "lucide-react";
import { uploadAdminImage } from "@/lib/api";
import { toast } from "sonner";

const base = "w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-emerald-100/40 focus:outline-none focus:ring-1 focus:ring-brass";

export const AInput = ({ label, value, onChange, placeholder, testId, type = "text" }) => (
  <div className="space-y-1">
    {label && <label className="text-xs text-emerald-100/60">{label}</label>}
    <input className={base} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
  </div>
);

export const ATextarea = ({ label, value, onChange, placeholder, testId, rows = 3 }) => (
  <div className="space-y-1">
    {label && <label className="text-xs text-emerald-100/60">{label}</label>}
    <textarea className={base} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
  </div>
);

export const ASelect = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1">
    {label && <label className="text-xs text-emerald-100/60">{label}</label>}
    <select className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export const AToggle = ({ label, value, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm text-emerald-100/80 cursor-pointer">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-brass" data-testid={testId} />
    {label}
  </label>
);

export const ImageInput = ({ label, value, onChange, testId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Images only");
    if (file.size > 10 * 1024 * 1024) return toast.error("Max 10MB");
    setBusy(true);
    try { const url = await uploadAdminImage(file); onChange(url); toast.success("Image uploaded"); }
    catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
      {label && <label className="text-xs text-emerald-100/60">{label}</label>}
      <div className="flex items-center gap-3">
        <div className="w-20 h-16 rounded-lg overflow-hidden bg-[#091E16] border border-white/10 shrink-0 grid place-items-center">
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <LinkIcon className="w-4 h-4 text-emerald-100/30" />}
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="flex gap-2">
            <button type="button" onClick={() => ref.current?.click()} data-testid={testId} className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 text-white rounded-lg px-3 py-1.5">
              <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload"}
            </button>
            {value && <button type="button" onClick={() => onChange("")} className="text-xs text-emerald-100/50 hover:text-white flex items-center gap-1"><X className="w-3.5 h-3.5" /> Clear</button>}
          </div>
          <input className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" />
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      </div>
    </div>
  );
};
